import React, { useState } from 'react';

type ActiveField = 'total' | 'nut';

interface OutsInputProps {
  /** Called with the parsed guesses when the user submits. */
  onSubmit: (totalOuts: number, nutOuts: number) => void;
  /** When true, inputs and keypad are locked (e.g. timer expired). */
  disabled?: boolean;
  /** Label shown on the submit button. */
  submitLabel?: string;
}

const MAX_DIGITS = 3;

/**
 * Two numeric guess fields (Total Outs / Nut Outs) backed by a shared
 * on-screen number keypad. Used by Competitive and Practice modes so guesses
 * can be entered without a hardware keyboard (mobile-friendly).
 */
const OutsInput: React.FC<OutsInputProps> = ({
  onSubmit,
  disabled = false,
  submitLabel = 'Submit',
}) => {
  const [total, setTotal] = useState('');
  const [nut, setNut] = useState('');
  const [active, setActive] = useState<ActiveField>('total');

  const setActiveValue = (updater: (prev: string) => string) => {
    if (active === 'total') {
      setTotal(updater);
    } else {
      setNut(updater);
    }
  };

  const handleDigit = (digit: string) => {
    if (disabled) return;
    setActiveValue(prev => (prev.length >= MAX_DIGITS ? prev : prev + digit));
  };

  const handleBackspace = () => {
    if (disabled) return;
    setActiveValue(prev => prev.slice(0, -1));
  };

  const handleClear = () => {
    if (disabled) return;
    setActiveValue(() => '');
  };

  const canSubmit = !disabled && total !== '' && nut !== '';

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(parseInt(total, 10), parseInt(nut, 10));
    setTotal('');
    setNut('');
    setActive('total');
  };

  const fieldStyle = (field: ActiveField): React.CSSProperties => ({
    flex: 1,
    padding: 'min(12px, 2.4vw)',
    borderRadius: 'min(8px, 1.6vw)',
    border: `2px solid ${active === field ? '#2196F3' : '#444'}`,
    backgroundColor: active === field ? 'rgba(33, 150, 243, 0.12)' : 'transparent',
    cursor: disabled ? 'default' : 'pointer',
    transition: 'all 0.15s ease',
    minWidth: 0,
  });

  const keyStyle: React.CSSProperties = {
    padding: 'min(12px, 2.4vw) 0',
    fontSize: 'min(1.4rem, 5.6vw)',
    backgroundColor: '#2a2a2a',
    color: 'white',
    border: '1px solid #444',
    borderRadius: 'min(8px, 1.6vw)',
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.5 : 1,
  };

  return (
    <div style={{ maxWidth: 'min(440px, 96vw)', margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: 'min(12px, 2.4vw)', marginBottom: 'min(16px, 3.2vw)' }}>
        <div style={fieldStyle('total')} onClick={() => !disabled && setActive('total')}>
          <div style={{ fontSize: 'min(0.8rem, 3.2vw)', color: '#3b82f6', marginBottom: '4px' }}>
            Total Outs
          </div>
          <div style={{ fontSize: 'min(1.6rem, 6.4vw)', fontWeight: 'bold', minHeight: '1.6em' }}>
            {total || '–'}
          </div>
        </div>
        <div style={fieldStyle('nut')} onClick={() => !disabled && setActive('nut')}>
          <div style={{ fontSize: 'min(0.8rem, 3.2vw)', color: '#22c55e', marginBottom: '4px' }}>
            Nut Outs
          </div>
          <div style={{ fontSize: 'min(1.6rem, 6.4vw)', fontWeight: 'bold', minHeight: '1.6em' }}>
            {nut || '–'}
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gap: 'min(8px, 1.6vw)',
          marginBottom: 'min(16px, 3.2vw)',
        }}
      >
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(digit => (
          <button key={digit} style={keyStyle} onClick={() => handleDigit(digit)} disabled={disabled}>
            {digit}
          </button>
        ))}
        <button style={keyStyle} onClick={() => handleDigit('0')} disabled={disabled}>
          0
        </button>
        <button style={keyStyle} onClick={handleClear} disabled={disabled} aria-label="Clear">
          C
        </button>
        <button style={keyStyle} onClick={handleBackspace} disabled={disabled} aria-label="Backspace">
          ⌫
        </button>
      </div>

      <button
        onClick={handleSubmit}
        disabled={!canSubmit}
        style={{
          width: '100%',
          padding: 'min(14px, 2.8vw)',
          fontSize: 'min(1.2rem, 4.8vw)',
          backgroundColor: canSubmit ? '#2196F3' : '#3a3a3a',
          color: 'white',
          border: 'none',
          borderRadius: 'min(8px, 1.6vw)',
          cursor: canSubmit ? 'pointer' : 'default',
          transition: 'background-color 0.15s ease',
        }}
      >
        {submitLabel}
      </button>
    </div>
  );
};

export default OutsInput;
